import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Boxes,
  Truck,
  Clock,
  MapPin,
  Recycle,
  CheckCircle2,
  ArrowRight,
  Tag,
  Layers,
  Lock,
  KeyRound,
  ShieldOff,
  ShoppingCart,
  ListChecks,
  CalendarCheck,
  Leaf,
  Building2,
  Sparkles,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const DEPT_NEARBY_LINKABLE = {
  Aigremont: null,
  Andelu: null,
  Bazemont: null,
  Crespières: null,
  Herbeville: null,
  "Les Alluets-le-Roi": null,
  "Mareil-sur-Mauldre": null,
};

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Charte d'Excellence : 3 engagements numérotés ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Charte d&apos;excellence
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Vos 3 engagements de livraison de cartons {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que la logistique urbaine dans la deuxième ville des
            Yvelines exige une rigueur absolue, notre service de
            livraison s&apos;engage contractuellement auprès de vous.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {[
            {
              num: "01",
              tag: "PONCTUALITÉ",
              title: "Zéro Retard",
              body: "Le trafic sur les axes de Sartrouville ne doit pas bloquer votre organisation. Nous validons un créneau horaire fixe. Si nous annonçons une livraison à 10h, notre chauffeur est devant chez vous à 10h pour que vous puissiez lancer vos cartons sans perdre une minute.",
              Icon: Clock,
            },
            {
              num: "02",
              tag: "INTÉGRITÉ",
              title: "Zéro Défaut",
              body: "Pas de cartons froissés ou humides à l'arrivée. L'intégralité de vos fournitures est transportée à plat, sanglée et protégée sous une bâche hermétique. Vous recevez un matériel dans un état d'usine impeccable, prêt pour un pliage à haute résistance.",
              Icon: ShieldCheck,
            },
            {
              num: "03",
              tag: "SERVICE",
              title: "Porte-à-Porte Intégral",
              body: "Nous ne laissons jamais vos colis sur le trottoir. Que vous habitiez au rez-de-chaussée d'un pavillon ou au dernier étage d'une résidence du Vieux-Pays sans ascenseur, nos livreurs déposent votre commande à plat directement à l'intérieur de votre logement.",
              Icon: Truck,
            },
          ].map(({ num, tag, title, body, Icon }) => (
            <div
              key={num}
              className="relative bg-[#0f1d2b] text-white rounded-2xl p-6 md:p-7 overflow-hidden"
            >
              <div className="absolute top-5 right-5 font-display font-extrabold text-[60px] leading-none text-white/10 select-none">
                N°{num}
              </div>
              <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#FF5722] text-white">
                <Icon className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="mt-5 text-[10.5px] tracking-[0.18em] uppercase font-extrabold text-[#FF5722]">
                {tag}
              </div>
              <h3 className="mt-1 font-display font-extrabold text-[22px] text-white leading-tight">
                {title}
              </h3>
              <p className="mt-4 text-[14px] text-white/80 leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Engagement Éco-Responsable −40% ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline inline-flex items-center gap-2" style={{ color: "#237cb5" }}>
            <Leaf className="h-3.5 w-3.5" strokeWidth={2.8} />
            Engagement éco-responsable
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            L&apos;Engagement Éco-Responsable : des cartons de seconde
            vie à <span className="text-[#FF5722]">−40 %</span>
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            {city.aCity}, l&apos;éco-citoyenneté s&apos;invite dans
            votre déménagement. Nous avons développé une filière
            d&apos;économie circulaire qui prouve que l&apos;on peut
            protéger ses biens tout en préservant la planète et son
            portefeuille.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              title: "Le réflexe anti-gaspillage",
              body: "Ces cartons de qualité industrielle n'ont servi qu'une seule fois lors de transferts de sièges sociaux ou de livraisons d'usines. Plutôt que de les envoyer au pilon alors qu'ils sont parfaits, nous leur redonnons une seconde vie légitime.",
              Icon: Recycle,
            },
            {
              title: "Une rigidité 100 % préservée",
              body: "Chaque carton reconditionné fait l'objet d'un examen structurel rigoureux. La double cannelure est testée pour garantir qu'elle conserve l'intégralité de sa résistance d'origine à l'écrasement vertical dans le camion. Vous emballez au même niveau de sécurité qu'avec du neuf.",
              Icon: CheckCircle2,
            },
            {
              title: "Un tarif engagé à −40 %",
              body: "En choisissant cette gamme, vous réduisez l'empreinte carbone de votre déménagement de près de la moitié tout en bénéficiant d'une remise immédiate de 40 % par rapport au prix du marché du neuf.",
              Icon: Tag,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-reconditionnes"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#FF5722] hover:bg-[#e84a17] text-white font-bold text-[14px] transition-colors"
            data-testid="city-sartrouville-cta-reconditionnes"
          >
            <Recycle className="h-4 w-4" strokeWidth={2.6} />
            Voir les cartons reconditionnés −40 %
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Commander en ligne en 3 étapes (timeline) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Commande en ligne
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment lancer votre commande en quelques clics ?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour vous faciliter la vie, nous avons développé une
            plateforme e-commerce fluide et intuitive. Préparer vos
            achats n&apos;a jamais été aussi simple et rapide.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 relative">
          {/* ligne dégradée connectant les étapes */}
          <div
            aria-hidden="true"
            className="hidden lg:block absolute top-12 left-[16.66%] right-[16.66%] h-0.5 bg-gradient-to-r from-[#237cb5] via-[#237cb5] to-[#FF5722]"
          />
          {[
            {
              num: "01",
              title: "Sélectionnez vos produits",
              body: "Naviguez sur notre boutique en ligne et ajoutez à votre panier vos cartons standards, cartons lourds, penderies ou nos kits complets adaptés à la surface de votre logement.",
              Icon: ShoppingCart,
              color: "#237cb5",
            },
            {
              num: "02",
              title: "Validez votre panier",
              body: "Ajustez les quantités nécessaires et complétez votre commande avec nos accessoires indispensables (adhésifs techniques, pistolets dévidoirs, film à bulles ou diables de manutention).",
              Icon: ListChecks,
              color: "#237cb5",
            },
            {
              num: "03",
              title: "Choisissez votre créneau",
              body: "Renseignez votre adresse précise à Sartrouville et sélectionnez la date ainsi que l'horaire de livraison qui s'intègrent le mieux dans votre emploi du temps. En moins de deux minutes, votre logistique est planifiée !",
              Icon: CalendarCheck,
              color: "#FF5722",
            },
          ].map(({ num, title, body, Icon, color }) => (
            <div key={num} className="relative z-10">
              <div className="flex justify-center">
                <span
                  className="flex items-center justify-center h-24 w-24 rounded-full border-[6px] border-white text-white font-display font-extrabold text-[28px] shadow-lg"
                  style={{ backgroundColor: color }}
                >
                  {num}
                </span>
              </div>
              <div className="mt-6 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 text-center">
                <div className="flex justify-center">
                  <span
                    className="flex items-center justify-center h-11 w-11 rounded-xl text-white"
                    style={{ backgroundColor: color }}
                  >
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                </div>
                <h3 className="mt-4 font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-sartrouville-cta-shop"
          >
            <ShoppingCart className="h-4 w-4" strokeWidth={2.6} />
            Lancer ma commande
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-sartrouville-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir les kits clé-en-main
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Paiement sécurisé 100% (fond sombre) ======= */}
    <section className="py-14 md:py-20 bg-[#0f1d2b] text-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.18em] font-bold text-white/80 mb-4">
            <Lock className="h-3.5 w-3.5" strokeWidth={2.6} />
            Paiement sécurisé
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-white leading-tight">
            Paiement sécurisé à 100 % : achetez l&apos;esprit
            tranquille
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-white/80 leading-relaxed">
            La sécurité de vos transactions en ligne est notre
            priorité absolue. Nous mettons à votre disposition un
            protocole de paiement de dernière génération pour vous
            garantir une sérénité totale.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              title: "Cryptage SSL avancé",
              body: "Notre site utilise le protocole de sécurisation SSL (Secure Sockets Layer). Toutes vos données bancaires sont instantanément cryptées et rendues totalement illisibles avant d'être transmises.",
              Icon: Lock,
            },
            {
              title: "Authentification 3D Secure",
              body: "Pour chaque commande, une validation supplémentaire vous est demandée via l'application de votre banque (ou par code SMS) pour confirmer que vous êtes bien l'auteur de l'achat, empêchant ainsi toute utilisation frauduleuse de votre carte.",
              Icon: KeyRound,
            },
            {
              title: "Zéro stockage de données",
              body: "Vos informations bancaires ne sont jamais stockées sur nos serveurs. La transaction est traitée directement de banque à banque, vous garantissant un achat 100 % sûr et protégé.",
              Icon: ShieldOff,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="rounded-2xl bg-white/5 border border-white/10 p-6 md:p-7"
            >
              <span className="flex items-center justify-center h-11 w-11 rounded-xl bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="mt-5 font-display font-extrabold text-[18px] text-white leading-tight">
                {title}
              </h3>
              <p className="mt-3 text-[14px] text-white/80 leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 6 — Cartons disponibles dans les Yvelines ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline inline-flex items-center gap-2" style={{ color: "#237cb5" }}>
            <Building2 className="h-3.5 w-3.5" strokeWidth={2.8} />
            Couverture Yvelines (78)
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Cartons de déménagement disponibles dans les Yvelines
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Que vous résidiez dans une grande ville ou une commune plus
            résidentielle des Yvelines, nous mettons à votre
            disposition nos cartons de déménagement et fournitures
            d&apos;emballage. Notre couverture départementale nous
            permet de répondre rapidement à vos besoins et de vous
            fournir le matériel nécessaire pour préparer votre
            déménagement dans les meilleures conditions.
          </p>
          <p className="mt-3 text-[14px] text-[#5b6b7a]">
            Découvrez les principales communes du 78 où nos produits
            sont régulièrement livrés.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {Object.entries(DEPT_NEARBY_LINKABLE).map(([name, slug]) => (
            <div
              key={name}
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#EEF3F7] border border-[#cfdbe6] text-[13px] font-semibold text-[#0f1d2b]"
            >
              <MapPin className="h-3.5 w-3.5 text-[#237cb5]" strokeWidth={2.6} />
              {name}
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-sartrouville-cta-dept-coverage"
          >
            <Sparkles className="h-4 w-4" strokeWidth={2.6} />
            Voir toute la couverture des Yvelines
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 7 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-sartrouville-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
